// Competitor Benchmark: "misal competitor sedang campaign apa di sosmed dan
// lain sebagainya". What competitors campaign on and where, as recorded by
// hand, plus a data-driven comparison only where this industry's dataset has
// a competitor column. Everything shown comes from GET /api/i/:slug/benchmark;
// the form posts to /api/i/:slug/benchmark/campaign.
//
// Honesty: the API's disclaimer sits at the top as a Notice; seeded rows
// carry a "Contoh" badge wherever they appear and every count that includes
// them says so; a null engagement reads "Tidak dicatat", never 0, and is
// never charted; the signals note is always one visible line.
//
// Section concepts (JEV ui.region_gate, 2026-10-12):
//   Papan      job: board size at a glance.                         one row of four KPI cards
//   Bauran     job: per competitor by status, where, about what.    one level row: stacked columns,
//                                                                    channel donut, focus tiles
//   Pesaing    job: compare competitors side by side.               rows in one card
//   Kampanye   job: read every recorded campaign with its evidence. rows in one card
//   Sinyal     job: own against competitor from the dataset.        per signal: four KPI cards, then
//                                                                    paired bars beside the standing
//   Catat      job: record one observed campaign with its source.   closed Disclosure
import {
  Badge,
  BarList,
  Disclosure,
  DonutChart,
  EmptyState,
  HeatTiles,
  KpiCard,
  ListRows,
  Notice,
  PageHeader,
  PairScatter,
  Panel,
  StackedColumns,
  VizBand,
  VizCard,
  VizFigure,
  VizRow,
} from "@dashboard-intelligent/ui";
import type { BenchmarkBoard, BenchmarkRow, Campaign, CompetitorSignal } from "@dashboard-intelligent/api/src/modules/benchmark/types";
import { formatMetric } from "../format";
import { useIndustry } from "../industry";
import { CampaignForm } from "./benchmark/CampaignForm";
import {
  STATUS_ORDER,
  STATUS_TEXT,
  channelLabels,
  channelText,
  engagementText,
  evidenceUrl,
  focusCounts,
  formatDate,
  gapParts,
  gapText,
  largestGaps,
  listText,
  newestFirst,
  periodText,
  sentence,
  standingColumns,
  standingOf,
  standingParts,
  statusByCompetitor,
} from "./benchmark/model";
import { useBoard } from "./benchmark/useBoard";

const count = (n: number) => formatMetric(n, "count");

/** Groups a paired chart draws in its card before the rest go to the disclosure. */
const CHART_ROWS_MAX = 8;

export function BenchmarkPage() {
  const { slug } = useIndustry();
  const board = useBoard(slug);

  return (
    <>
      <PageHeader
        title="Competitor Benchmark"
        description="Kampanye pesaing yang dicatat tim, di kanal mana, dan perbandingan dengan pesaing bila datanya ada."
      />
      {board.status === "loading" ? (
        <Panel title="Papan pesaing">
          <div aria-busy="true">
            <EmptyState icon="benchmark" title="Memuat papan pesaing">
              Catatan kampanye dan sinyal pesaing untuk industri ini sedang diambil.
            </EmptyState>
          </div>
        </Panel>
      ) : board.status === "error" ? (
        <Notice
          tone="danger"
          title="Papan pesaing tidak dapat dimuat"
          action={
            <button type="button" className="btn-secondary" onClick={board.reload}>
              Coba lagi
            </button>
          }
        >
          <p>{board.error}</p>
        </Notice>
      ) : (
        <Board board={board.data} onSaved={board.refresh} />
      )}
    </>
  );
}

function Board({ board, onSaved }: { board: BenchmarkBoard; onSaved: () => void }) {
  const { slug } = useIndustry();
  const labels = channelLabels(board);
  const names = new Map(board.competitors.map((c) => [c.id, c.name]));
  return (
    <>
      <Notice tone="info" title="Papan ini diisi tangan">
        <p>{board.disclaimer}</p>
      </Notice>
      <BoardFigures board={board} />
      {board.campaigns.length > 0 ? (
        <MixRow board={board} />
      ) : (
        <VizRow>
          <VizCard span={12} title="Kampanye tercatat" cardId="kampanye-kosong">
            <EmptyState icon="benchmark" title="Belum ada kampanye tercatat">
              Buka Catat kampanye pesaing di bagian bawah halaman ini untuk mencatat yang pertama.
            </EmptyState>
          </VizCard>
        </VizRow>
      )}
      <VizRow>
        <ComparisonCard rows={board.rows} labels={labels} />
      </VizRow>
      {board.campaigns.length > 0 ? (
        <VizRow>
          <CampaignsCard campaigns={board.campaigns} labels={labels} competitorName={(id) => names.get(id) ?? id} />
        </VizRow>
      ) : null}

      {/* Always one plain line: what the signals cover, or why there are none. Never an empty panel. */}
      <p className="viz-muted" data-signals={board.signals.length > 0 ? "" : undefined}>
        <strong>Sinyal pesaing.</strong> {board.signalsNote}
      </p>
      {board.signals.map((signal) => (
        <SignalBand key={signal.id} signal={signal} />
      ))}

      <Disclosure
        title="Catat kampanye pesaing"
        description="Isi dari pengamatan Anda sendiri, dengan sumbernya. Semua kolom wajib kecuali yang bertanda opsional."
      >
        <CampaignForm slug={slug} competitors={board.competitors} channels={board.byChannel} onSaved={onSaved} />
      </Disclosure>
    </>
  );
}

function ExampleBadge() {
  return (
    <Badge tone="warning" icon="info">
      Contoh
    </Badge>
  );
}

/** How many of a counted set are seeded examples, so a figure never passes them off as observations. */
function exampleNote(examples: number, total: number): string | undefined {
  if (examples === 0) return undefined;
  return examples === total ? "Semuanya baris Contoh." : `${count(examples)} di antaranya Contoh.`;
}

/** The size of the board: four figures of one anatomy. */
function BoardFigures({ board }: { board: BenchmarkBoard }) {
  const total = board.campaigns.length;
  const running = board.campaigns.filter((c) => c.status === "running").length;
  const rivals = board.rows.filter((r) => !r.isSelf);
  const active = rivals.filter((r) => r.activeCampaigns > 0).length;
  const used = board.byChannel.filter((c) => c.count > 0).length;
  const examples = board.campaigns.filter((c) => c.isExample).length;
  return (
    <VizRow label="Ukuran papan pesaing">
      <KpiCard
        span={3}
        cardId="kampanye-berjalan"
        label="Kampanye berjalan"
        value={count(running)}
        unit={`dari ${count(total)}`}
        status={examples > 0 ? <ExampleBadge /> : undefined}
        context={exampleNote(examples, total) ?? "Semua hasil pengamatan tim."}
      />
      <KpiCard
        span={3}
        cardId="pesaing-dipantau"
        label="Pesaing dipantau"
        value={count(rivals.length)}
        status={rivals.some((r) => r.isExample) ? <ExampleBadge /> : undefined}
        context={`${count(active)} punya kampanye berjalan`}
      />
      <KpiCard
        span={3}
        cardId="kanal-dipakai"
        label="Kanal dipakai"
        value={count(used)}
        unit={`dari ${count(board.byChannel.length)}`}
        context={used > 0 ? "Kanal dengan kampanye tercatat" : "Belum ada kanal dengan kampanye"}
      />
      <KpiCard
        span={3}
        cardId="sinyal-data"
        label="Sinyal dari dataset"
        value={count(board.signals.length)}
        context={board.signals.length > 0 ? "Dihitung dari kolom pesaing" : "Dataset tanpa kolom pesaing"}
      />
    </VizRow>
  );
}

/** Who campaigns in which state, where, and about what: one level row. */
function MixRow({ board }: { board: BenchmarkBoard }) {
  const rows = statusByCompetitor(board);
  const used = board.byChannel.filter((c) => c.count > 0);
  const focus = focusCounts(board.campaigns);
  const flagged = board.campaigns.some((c) => c.isExample) ? "Termasuk baris Contoh" : undefined;
  const busiest = rows.reduce((best, r) => (r.total > best.total ? r : best), rows[0]!);
  const topChannel = [...used].sort((a, b) => b.count - a.count)[0];
  const topFocus = [...focus].sort((a, b) => b.value - a.value)[0];
  return (
    <VizRow label="Bauran kampanye pesaing">
      <VizCard
        span={4}
        title="Kampanye per pesaing"
        description={flagged ? `${count(rows.length)} pesaing, ${flagged.toLowerCase()}` : `${count(rows.length)} pesaing menurut status`}
        figure={<VizFigure value={count(busiest.total)} label={`kampanye, terbanyak ${busiest.name}`} />}
        cardId="per-pesaing"
      >
        <StackedColumns
          label="Kampanye tercatat per pesaing dan status"
          categoryLabel="Pesaing"
          format={count}
          data={rows.map((r) => ({ label: r.label, running: r.running, planned: r.planned, ended: r.ended }))}
          series={STATUS_ORDER.map((status) => ({ key: status, label: STATUS_TEXT[status] }))}
        />
      </VizCard>
      <VizCard
        span={4}
        title="Bauran kanal"
        description={flagged ? `${count(used.length)} dari ${count(board.byChannel.length)} kanal dipakai, ${flagged.toLowerCase()}` : `${count(used.length)} dari ${count(board.byChannel.length)} kanal dipakai`}
        figure={topChannel ? <VizFigure value={count(topChannel.count)} label={`kampanye, terbanyak di ${topChannel.label}`} /> : undefined}
        cardId="bauran-kanal"
      >
        {used.length >= 2 ? (
          <DonutChart label="Kampanye tercatat per kanal" data={used.map((c) => ({ label: c.label, value: c.count }))} format={count} categoryLabel="Kanal" valueLabel="Kampanye" />
        ) : (
          <BarList label="Kampanye tercatat per kanal" data={used.map((c) => ({ label: c.label, value: c.count }))} format={count} categoryLabel="Kanal" valueLabel="Kampanye" />
        )}
      </VizCard>
      <VizCard
        span={4}
        title="Fokus kampanye"
        description={focus.length > 0 ? `${count(focus.length)} fokus${flagged ? `, ${flagged.toLowerCase()}` : " dari kampanye tercatat"}` : "Belum ada fokus tercatat"}
        figure={topFocus ? <VizFigure value={count(topFocus.value)} label={`kampanye, terbanyak ${topFocus.label}`} /> : undefined}
        cardId="fokus"
      >
        {focus.length > 0 ? (
          <HeatTiles label="Kampanye tercatat per fokus" data={focus} format={count} categoryLabel="Fokus" valueLabel="Kampanye" />
        ) : (
          <p className="viz-muted">Isi kolom Fokus saat mencatat kampanye agar sebarannya terlihat di sini.</p>
        )}
      </VizCard>
    </VizRow>
  );
}

/** One row per competitor: who it is, where and on what it campaigns, when it last began, how many run now. */
function ComparisonCard({ rows, labels }: { rows: BenchmarkRow[]; labels: Record<string, string> }) {
  return (
    <VizCard span={12} title="Perbandingan pesaing" description={`${count(rows.length)} baris, termasuk Anda sendiri`} variant="flush" cardId="perbandingan">
      {rows.length > 0 ? (
        <ListRows
          label="Perbandingan pesaing"
          items={rows.map((r) => {
            const recorded = r.totalCampaigns > 0;
            return {
              id: r.competitorId,
              title: (
                <span className="viz-inline">
                  <span className="viz-strong">{r.name}</span>
                  {r.isExample ? <ExampleBadge /> : null}
                  {r.isSelf ? <Badge>Sendiri</Badge> : null}
                </span>
              ),
              description: recorded
                ? [
                    `Kanal: ${listText(r.channels.map((c) => channelText(c, labels)))}.`,
                    `Fokus: ${listText(r.focus, "tidak dicatat")}.`,
                    r.lastSeen ? `Terakhir mulai ${formatDate(r.lastSeen)}.` : "",
                  ]
                    .filter(Boolean)
                    .join(" ")
                : "Belum ada kampanye tercatat.",
              trailing: recorded ? (
                <span className="viz-value">
                  {count(r.activeCampaigns)} dari {count(r.totalCampaigns)} berjalan
                </span>
              ) : undefined,
            };
          })}
        />
      ) : (
        <EmptyState icon="benchmark" title="Belum ada pesaing">
          Papan ini belum memuat pesaing.
        </EmptyState>
      )}
    </VizCard>
  );
}

function CampaignsCard({
  campaigns,
  labels,
  competitorName,
}: {
  campaigns: Campaign[];
  labels: Record<string, string>;
  competitorName: (id: string) => string;
}) {
  return (
    <VizCard span={12} title="Kampanye tercatat" description={`${count(campaigns.length)} kampanye, yang terakhir dicatat di atas`} variant="flush" cardId="kampanye">
      <ListRows
        label="Kampanye pesaing yang tercatat"
        items={newestFirst(campaigns).map((c) => ({
          id: c.id,
          title: (
            <span className="viz-inline">
              <span className="viz-strong">{c.title}</span>
              {c.isExample ? <ExampleBadge /> : null}
              <Badge tone={c.status === "running" ? "info" : "neutral"}>{STATUS_TEXT[c.status]}</Badge>
            </span>
          ),
          description: c.angle,
          meta: <CampaignMeta campaign={c} channel={channelText(c.channel, labels)} competitor={competitorName(c.competitorId)} />,
        }))}
      />
    </VizCard>
  );
}

function CampaignMeta({ campaign, channel, competitor }: { campaign: Campaign; channel: string; competitor: string }) {
  const url = evidenceUrl(campaign.evidence);
  return (
    <span className="viz-meta">
      <span>
        {competitor} di {channel}, {periodText(campaign)}
      </span>
      <span>Fokus: {listText(campaign.focus, "tidak dicatat")}</span>
      <span>
        Bukti:{" "}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {campaign.evidence}
          </a>
        ) : (
          campaign.evidence
        )}
      </span>
      <span>
        Dicatat oleh {campaign.recordedBy}, {formatDate(campaign.recordedOn)}. Engagement: {engagementText(campaign.engagement)}
      </span>
    </span>
  );
}

/**
 * One data-driven signal as a band: four figures (own, competitor, the gap,
 * the rows compared), then own against the competitor per group beside the
 * standing, and how it is computed in a closed disclosure.
 */
function SignalBand({ signal }: { signal: CompetitorSignal }) {
  const fmt = (n: number) => formatMetric(n, signal.format);
  const group = signal.groupLabel.toLowerCase();
  const groups = signal.byGroup.length;
  const capped = groups > CHART_ROWS_MAX;
  const gap = gapParts(signal.gap, signal.format);
  const standing = standingOf(signal.byGroup);
  const parts = standingParts(standing);
  const ordered = largestGaps(signal.byGroup);
  return (
    <VizBand title={signal.title} meta="Sinyal dari dataset" id={`sinyal-${signal.id}`}>
      <VizRow label={`Angka sinyal ${signal.title}`}>
        <KpiCard span={3} label={signal.ownLabel} value={fmt(signal.own)} context={`Pesaing ${fmt(signal.competitor)}`} cardId={`${signal.id}-own`} />
        <KpiCard span={3} label={signal.competitorLabel} value={fmt(signal.competitor)} context={`Sendiri ${fmt(signal.own)}`} cardId={`${signal.id}-competitor`} />
        <KpiCard
          span={3}
          label="Selisih, sendiri dikurangi pesaing"
          value={gap.value}
          unit={gap.unit}
          context={sentence(gapText(signal.gap, signal.format))}
          cardId={`${signal.id}-gap`}
        />
        <KpiCard span={3} label="Baris dibandingkan" value={count(signal.rows)} context="Baris yang mencatat keduanya" cardId={`${signal.id}-rows`} />
      </VizRow>
      {groups > 0 ? (
        <VizRow label={`${signal.title} per ${group}`}>
          <VizCard
            span={8}
            title={`${signal.ownLabel} dan pesaing per ${group}`}
            description={`${count(groups)} ${group}, satu titik per ${group}`}
            cardId={`${signal.id}-per-kelompok`}
          >
            <PairScatter
              label={`${signal.title} per ${group}`}
              data={ordered.map((p) => ({ label: p.label, a: p.own, b: p.competitor }))}
              format={fmt}
              categoryLabel={signal.groupLabel}
              aLabel="Sendiri"
              bLabel="Pesaing"
            />
          </VizCard>
          <VizCard
            span={4}
            title={`Posisi per ${group}`}
            description="Terhadap pesaing"
            figure={<VizFigure value={`${count(standing.below)} dari ${count(groups)}`} label={`${group} di bawah pesaing`} />}
            cardId={`${signal.id}-posisi`}
          >
            {parts.length >= 2 ? (
              <DonutChart label={`${signal.groupLabel} menurut posisi terhadap pesaing`} data={parts} format={count} categoryLabel="Posisi" valueLabel={signal.groupLabel} />
            ) : (
              <BarList
                label={`${signal.groupLabel} menurut posisi terhadap pesaing`}
                data={standingColumns(standing)}
                format={count}
                categoryLabel="Posisi"
                valueLabel={signal.groupLabel}
                sort={false}
              />
            )}
          </VizCard>
        </VizRow>
      ) : (
        <p className="viz-muted">{signal.summary}</p>
      )}
      <Disclosure title={capped ? `Cara hitung dan semua ${count(groups)} ${group}` : "Cara hitung dan sumber data"} description={signal.summary}>
        <p className="viz-detail-text">Cara hitung: {signal.basis}</p>
        <p className="viz-detail-text">
          Dihitung dari {count(signal.rows)} baris. Kolom: {signal.sources.join(", ")}.
        </p>
        {capped ? (
          <ListRows
            label={`Semua ${group}`}
            items={ordered.map((p, i) => ({
              id: `${signal.id}-${i}`,
              title: p.label,
              trailing: (
                <span className="viz-value">
                  {fmt(p.own)} lawan {fmt(p.competitor)}
                </span>
              ),
            }))}
          />
        ) : null}
      </Disclosure>
    </VizBand>
  );
}
